/**
 * API Configuration and client utilities.
 * Centralize all API endpoints and fetch logic for easier maintenance and testing.
 */

// API base URL - use environment variable if available (set at build time in Railway).
// Falls back to the known production backend URL so the app works even if the
// env var is missing from the build environment.
const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE;
export const API_BASE = REACT_APP_API_BASE || 'https://sqip-production.up.railway.app/api';

// Endpoint paths (grouped by domain for clarity)
export const ENDPOINTS = {
  // Auth/User
  ME: `${API_BASE}/me/`,
  AUTH: `${API_BASE}/auth/`,
  PASSWORD_LOGIN: `${API_BASE}/auth/password/`,
  FORGOT_PASSWORD: `${API_BASE}/auth/forgot-password/`,
  RESET_PASSWORD: `${API_BASE}/auth/reset-password/`,
  ADMIN_PASSWORD_RESET: `${API_BASE}/admin/password-reset/`,
  USERNAME_AVAILABLE: (username, excludeSelf = false) =>
    `${API_BASE}/auth/username-available/?username=${encodeURIComponent(username)}${excludeSelf ? '&exclude_self=1' : ''}`,
  MY_CREDENTIALS: `${API_BASE}/me/credentials/`,
  REGISTER: `${API_BASE}/register/`,
  VALIDATE_TOKEN: `${API_BASE}/validate/token/`,
  CONFIG: `${API_BASE}/config/`,
  SEND_OTP: `${API_BASE}/send/otp/`,
  VERIFY_OTP: `${API_BASE}/verify/otp/`,
  CONSENT_STATUS: `${API_BASE}/otp/consent-status/`,

  // Contact / support
  CONTACT: `${API_BASE}/contact/`,
  FEEDBACK: `${API_BASE}/feedback/`,

  // Subscription packages / tiers
  PACKAGES: `${API_BASE}/packages/`,
  TRIAL_PACKAGES: `${API_BASE}/packages/?trials=1`,

  // Appointments
  APPOINTMENTS: `${API_BASE}/appointments/`,
  APPOINTMENTS_ALL: (pageSize = 50, page = 1) =>
    `${API_BASE}/appointments/?type=all&page_size=${pageSize}&page=${page}`,
  // For user-facing pages (uses ?type= query param)
  APPOINTMENTS_UNSCHEDULED: (pageSize = 50, page = 1) =>
    `${API_BASE}/appointments/?type=unscheduled&page_size=${pageSize}&page=${page}`,
  APPOINTMENTS_SCHEDULED: (pageSize = 50, page = 1) =>
    `${API_BASE}/appointments/?type=scheduled&page_size=${pageSize}&page=${page}`,
  // For admin pages (uses actual /scheduled/ and /unscheduled/ endpoints)
  APPOINTMENTS_SCHEDULED_LIST: (pageSize = 100) =>
    `${API_BASE}/appointments/scheduled/?page_size=${pageSize}`,
  APPOINTMENTS_UNSCHEDULED_LIST: (pageSize = 100) =>
    `${API_BASE}/appointments/unscheduled/?page_size=${pageSize}`,
  APPOINTMENTS_UNSCHEDULE: `${API_BASE}/appointments/unschedule/`,
  APPOINTMENTS_UNSCHEDULED_COUNT: (categoryId) =>
    `${API_BASE}/appointments/unscheduled-count/?category_id=${categoryId}&status=active`,
  APPOINTMENTS_ADD_TO_QUEUE: `${API_BASE}/appointments/add_user_to_queue/`,
  APPOINTMENT_NOTES: (appointmentId) => `${API_BASE}/appointments/${appointmentId}/notes/`,
  APPOINTMENT_NOTE_FILE: (appointmentId, noteId) =>
    `${API_BASE}/appointments/${appointmentId}/notes/${noteId}/file/`,
  APPOINTMENT_CANCEL: (appointmentId) => `${API_BASE}/appointments/${appointmentId}/cancel/`,
  APPOINTMENT_CHECKIN: (appointmentId) => `${API_BASE}/appointments/${appointmentId}/check-in/`,
  APPOINTMENT_CHECKOUT: (appointmentId) => `${API_BASE}/appointments/${appointmentId}/checkout/`,
  APPOINTMENT_MOVE: (appointmentId) => `${API_BASE}/appointments/${appointmentId}/move/`,
  APPOINTMENT_DETAIL: (appointmentId) => `${API_BASE}/appointments/${appointmentId}/`,
  APPOINTMENTS_BULK_ACTION: `${API_BASE}/appointments/bulk-action/`,

  // Organizations
  ORGANIZATIONS: (pageSize = 100) => `${API_BASE}/organizations/?page_size=${pageSize}`,
  // Optional `loc` adds location awareness: { lat, lng, radius_km, city, state, country }.
  // All optional — omitted params just fall back to the plain text/name ordering.
  ORGANIZATIONS_ACTIVE: (q, page, loc = {}) => {
    const params = new URLSearchParams({ search: q || '', page_size: '20', page: String(page) });
    // NOTE: city/state/country ride under near_* so they only influence RANKING. Sending bare
    // city/state/country would hit OrganizationFilter and hard-filter results (a search bug).
    const map = { lat: 'lat', lng: 'lng', radius_km: 'radius_km',
      city: 'near_city', state: 'near_state', country: 'near_country' };
    Object.entries(map).forEach(([key, param]) => {
      const v = loc[key];
      if (v !== undefined && v !== null && v !== '') params.set(param, String(v));
    });
    return `${API_BASE}/organizations/active/?${params.toString()}`;
  },
  // Tier-1 (no-permission) location from the caller's IP. Returns {} when unavailable.
  ORG_MY_LOCATION: `${API_BASE}/organizations/my-location/`,
  // Distinct city/state/country across active orgs, for the search filter dropdowns.
  ORG_LOCATION_OPTIONS: `${API_BASE}/organizations/location-options/`,
  ORGANIZATION_LANDING: (orgId) => `${API_BASE}/organizations/${orgId}/landing/`,
  ORGANIZATION_ADMINS: (orgId) => `${API_BASE}/organizations/${orgId}/admins/`,
  ORGANIZATION_CATEGORIES: (orgId, pageSize = 100) =>
    `${API_BASE}/organizations/${orgId}/categories/?page_size=${pageSize}`,
  ORGANIZATION_DELETE_CATEGORY_USER: (orgId) =>
    `${API_BASE}/organizations/${orgId}/delete-category-user/`,
  ORGANIZATION_DELETE_CATEGORY_USER_ACCOUNT: (orgId) =>
    `${API_BASE}/organizations/${orgId}/delete-category-user-account/`,

  // Categories
  CATEGORIES: (pageSize = 100) => `${API_BASE}/categories/?page_size=${pageSize}`,
  CATEGORY_DETAIL: (id) => `${API_BASE}/categories/${id}/`,
  CATEGORIES_USER: (pageSize = 100) => `${API_BASE}/categories/user/?page_size=${pageSize}`,
  CATEGORIES_ACTIVE: (orgId, pageSize = 50, page = 1) =>
    `${API_BASE}/categories/active/?organization=${orgId}&page_size=${pageSize}&page=${page}`,
  // All categories for an org, readable by any authenticated admin (used by insights filters).
  CATEGORIES_BY_ORG: (orgId) => `${API_BASE}/categories/?organization=${orgId}&page_size=200`,
  CATEGORY_DETAIL: (categoryId) => `${API_BASE}/categories/${categoryId}/`,
  CATEGORY_UPDATE_STATUS: (categoryId) => `${API_BASE}/categories/${categoryId}/update-status/`,
  CATEGORY_UPDATE_INFO: (categoryId) => `${API_BASE}/categories/${categoryId}/update-info/`,

  // Category facades — display-only groupings an org admin sets up in the admin panel.
  FACADES_BY_ORG: (orgId) => `${API_BASE}/facades/?organization=${orgId}&page_size=200`,
  // Public: active facades only, for the booking/search pages (readable while anonymous).
  FACADES_ACTIVE_BY_ORG: (orgId) => `${API_BASE}/facades/?organization=${orgId}&status=active&page_size=200`,

  // Booking invites — admin texts/emails a customer a join link; consume is public.
  SEND_INVITE: (orgId) => `${API_BASE}/organizations/${orgId}/send-invite/`,
  INVITE_CONSUME: (token) => `${API_BASE}/invites/${token}/`,
  FACADES: `${API_BASE}/facades/`,
  FACADE_DETAIL: (facadeId) => `${API_BASE}/facades/${facadeId}/`,
  FACADE_SET_CATEGORIES: (facadeId) => `${API_BASE}/facades/${facadeId}/set-categories/`,

  // Onboarding funnel — public
  ONBOARDING_ENQUIRY: `${API_BASE}/onboarding/enquiry/`,
  ONBOARDING_CHECKOUT: (reference) => `${API_BASE}/onboarding/checkout/${reference}/`,
  ONBOARDING_CHECKOUT_PAY: (reference) => `${API_BASE}/onboarding/checkout/${reference}/pay/`,
  MAGIC_LINK_CONSUME: (token) => `${API_BASE}/auth/magic-link/consume/${token}/`,

  // Onboarding admin panel (sqip admin)
  ONBOARDING_LIST: (qs = '') => `${API_BASE}/onboarding/${qs ? `?${qs}` : ''}`,
  ONBOARDING_SEND_EMAIL: (id) => `${API_BASE}/onboarding/${id}/send-onboard-email/`,
  ONBOARDING_EXTEND_TRIAL: (id) => `${API_BASE}/onboarding/${id}/extend-trial/`,
  ONBOARDING_CANCEL_TRIAL: (id) => `${API_BASE}/onboarding/${id}/cancel-trial/`,
  ONBOARDING_REACTIVATE: (id) => `${API_BASE}/onboarding/${id}/reactivate/`,
  ONBOARDING_DELETE: (id) => `${API_BASE}/onboarding/${id}/delete/`,
  ONBOARDING_UPDATE: (id) => `${API_BASE}/onboarding/${id}/`,
  // Save the trial/pay-first offer; activates an already-checked-out business when trial is on.
  ONBOARDING_APPLY_OFFER: (id) => `${API_BASE}/onboarding/${id}/apply-offer/`,
  ONBOARDING_INSIGHTS: `${API_BASE}/onboarding/insights/`,
  // Sqip-admin manual activation toggle (org optionally cascades to its categories).
  ONBOARDING_ORG_STATUS: (orgId) => `${API_BASE}/onboarding/orgs/${orgId}/status/`,
  ONBOARDING_CATEGORY_STATUS: (catId) => `${API_BASE}/onboarding/categories/${catId}/status/`,
  // Paginated + searchable org list (sqip admin notification-settings picker).
  ORG_LIST: (page = 1, q = '') => `${API_BASE}/organizations/?page=${page}&page_size=10${q ? `&search=${encodeURIComponent(q)}` : ''}`,

  // Org billing + insights (org admin for their own org; sqip admin for any)
  ORG_DETAIL: (orgId) => `${API_BASE}/organizations/${orgId}/`,
  ORG_SUBSCRIPTION: (orgId) => `${API_BASE}/organizations/${orgId}/subscription/`,
  ORG_CHOOSE_PACKAGE: (orgId) => `${API_BASE}/organizations/${orgId}/choose-package/`,
  ORG_UPDATE_INFO: (orgId) => `${API_BASE}/organizations/${orgId}/update-info/`,
  ORG_DISPLAY_PICTURE: (orgId) => `${API_BASE}/organizations/${orgId}/display-picture/`,
  ORG_CANCEL_SUBSCRIPTION: (orgId) => `${API_BASE}/organizations/${orgId}/cancel-subscription/`,
  ORG_CANCEL_SCHEDULED_CHANGE: (orgId) => `${API_BASE}/organizations/${orgId}/cancel-scheduled-change/`,
  ORG_RESUME_SUBSCRIPTION: (orgId) => `${API_BASE}/organizations/${orgId}/resume-subscription/`,
  ORG_SUBSCRIBE: (orgId) => `${API_BASE}/organizations/${orgId}/subscribe/`,
  ORG_BILLING_PORTAL: (orgId) => `${API_BASE}/organizations/${orgId}/billing-portal/`,
  // Embedded billing suite
  ORG_PAYMENT_METHODS: (orgId) => `${API_BASE}/organizations/${orgId}/payment-methods/`,
  ORG_SETUP_INTENT: (orgId) => `${API_BASE}/organizations/${orgId}/setup-intent/`,
  ORG_SET_DEFAULT_PM: (orgId) => `${API_BASE}/organizations/${orgId}/set-default-payment-method/`,
  ORG_REMOVE_PM: (orgId) => `${API_BASE}/organizations/${orgId}/remove-payment-method/`,
  ORG_INVOICES: (orgId) => `${API_BASE}/organizations/${orgId}/invoices/`,
  ORG_RETRY_PAYMENT: (orgId) => `${API_BASE}/organizations/${orgId}/retry-payment/`,
  ORG_BILLING_DETAILS: (orgId) => `${API_BASE}/organizations/${orgId}/billing-details/`,
  ORG_APPLY_COUPON: (orgId) => `${API_BASE}/organizations/${orgId}/apply-coupon/`,
  ORG_NOTIFICATION_SETTINGS: (orgId) => `${API_BASE}/organizations/${orgId}/notification-settings/`,
  ORG_PAYMENTS: (orgId, page = 1) => `${API_BASE}/organizations/${orgId}/payments/?page=${page}`,
  ORG_USAGE_HISTORY: (orgId, page = 1) => `${API_BASE}/organizations/${orgId}/usage-history/?page=${page}`,
  ORG_INSIGHTS: (orgId, qs = '') => `${API_BASE}/organizations/${orgId}/insights/${qs ? `?${qs}` : ''}`,
  // utils/api.js
    ONBOARDING_CHECKOUT_PAY: (reference) => `${API_BASE}/onboarding/checkout/${reference}/pay/`,
    CHECKOUT_SESSION_STATUS: `${API_BASE}/payments/stripe/session-status/`,
};

/**
 * Fetch wrapper that handles common auth and error logic.
 * @param {string} url - API endpoint URL
 * @param {object} options - fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} - fetch response
 */
export async function apiFetch(url, options = {}) {
  const token = localStorage.getItem('accessToken');
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response = await fetch(url, { ...options, headers });

  // If access token expired (401), try to refresh using refresh token and retry once.
  if (response.status === 401) {
    const refreshToken = localStorage.getItem('refreshToken');
    // First try: if an explicit refresh token exists in localStorage (backward compat)
    if (refreshToken) {
      try {
        const r = await fetch(`${API_BASE}/token/refresh/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        });
        if (r.ok) {
          const data = await r.json();
          // store new access token; preserve refresh token (may rotate depending on backend)
          if (data.access) {
            localStorage.setItem('accessToken', data.access);
            const payload = parseJwt(data.access);
            if (payload && payload.exp) {
              localStorage.setItem('accessTokenExp', String(payload.exp * 1000));
            }
          }
          // Retry original request with new access token
          const newHeaders = { ...headers, Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
          response = await fetch(url, { ...options, headers: newHeaders });
          return response;
        }
      } catch (e) {
        // fall through to try cookie-based refresh
      }
    }

    // Second try: attempt cookie-based refresh (HttpOnly cookie set by backend).
    try {
      const r2 = await fetch(`${API_BASE}/token/refresh/`, {
        method: 'POST',
        credentials: 'include',
      });
      if (r2.ok) {
        const data = await r2.json();
        if (data.access) {
          localStorage.setItem('accessToken', data.access);
          const payload = parseJwt(data.access);
          if (payload && payload.exp) {
            localStorage.setItem('accessTokenExp', String(payload.exp * 1000));
          }
        }
        const newHeaders = { ...headers, Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
        response = await fetch(url, { ...options, headers: newHeaders });
        return response;
      }
    } catch (e) {
      // fall through to return original 401
    }
  }

  return response;
}

/**
 * Helper to parse API responses and handle errors.
 * @param {Response} res - fetch response
 * @returns {Promise<object>} - parsed JSON or error object
 */
export async function parseApiResponse(res) {
  if (!res.ok) {
    let errorBody = {};
    let errorMessage = null;
    
    try {
      errorBody = await res.json();
    } catch (e) {
      // If response isn't JSON, try to get text
      try {
        const text = await res.text();
        if (text) {
          errorMessage = text;
        }
      } catch {
        // If that fails too, we'll use generic message below
      }
    }
    
    // If we don't have an error message yet, try to extract from error body
    if (!errorMessage) {
      // Check for nested errors object: {"errors": {"field": ["message"]}}
      if (errorBody.errors && typeof errorBody.errors === 'object') {
        for (const [field, msgs] of Object.entries(errorBody.errors)) {
          if (Array.isArray(msgs) && msgs.length > 0) {
            errorMessage = msgs[0];
            break;
          } else if (typeof msgs === 'string') {
            errorMessage = msgs;
            break;
          }
        }
      }
      // Check for simple message/error/detail fields
      if (!errorMessage) {
        if (errorBody.message) {
          errorMessage = errorBody.message;
        } else if (errorBody.error) {
          // error may be a string or a nested object like { message: '...' }
          errorMessage = typeof errorBody.error === 'string'
            ? errorBody.error
            : (errorBody.error.message || errorBody.error.detail || JSON.stringify(errorBody.error));
        } else if (errorBody.detail) {
          errorMessage = errorBody.detail;
        } else if (errorBody.non_field_errors && Array.isArray(errorBody.non_field_errors)) {
          errorMessage = errorBody.non_field_errors[0];
        } else if (typeof errorBody === 'object' && Object.keys(errorBody).length > 0) {
          // Look for first error in dict of field errors (top-level, not nested in .errors)
          for (const [field, msgs] of Object.entries(errorBody)) {
            if (field !== 'errors') {
              if (Array.isArray(msgs) && msgs.length > 0) {
                errorMessage = `${field}: ${msgs[0]}`;
                break;
              } else if (typeof msgs === 'string') {
                errorMessage = `${field}: ${msgs}`;
                break;
              }
            }
          }
        }
      }
    }
    
    // Fall back to HTTP status message if still no error message
    if (!errorMessage) {
      const statusText = res.statusText || 'Unknown error';
      errorMessage = statusText !== 'OK' && statusText ? statusText : `HTTP ${res.status}`;
    }
    
    const error = new Error(errorMessage);
    error.status = res.status;
    error.body = errorBody;
    throw error;
  }
  // 204/205 (e.g. DELETE) and any empty body have no JSON to parse — return null instead of
  // throwing "Unexpected end of JSON input".
  if (res.status === 204 || res.status === 205) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

/**
 * Convenience wrapper: fetch + parse in one call.
 * @param {string} url - API endpoint URL
 * @param {object} options - fetch options
 * @returns {Promise<object>} - parsed JSON response
 */
export async function apiCall(url, options = {}) {
  const res = await apiFetch(url, options);
  return parseApiResponse(res);
}

/**
 * Parse a JWT and return its payload as an object.
 * Returns null on any parse error.
 */
export function parseJwt(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // base64url -> base64
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}
